import Button from "@components/_Layout/Controls/Button";
import PropTypes from "prop-types";
import React from "react";
import SearchDates from "./SearchDates";
import SearchDestination from "./SearchDestination";
import SearchPax from "./SearchPax";
import SearchRoomTypeCategory from "./SearchRoomTypeCategory";
import { addDaysToDate } from "@utils/dates";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SelectOptionType } from "../../../../types";
import { useSearchBar, useSession } from "@hooks";

export type SearchBarProps = {
  isDateRange?: boolean;
  showDuration?: boolean;
  isMultiDestination?: boolean;
  showBabies?: boolean;
  showChildren?: boolean;
  isSearching?: boolean;
  handler?: (e: React.FormEvent) => void;
};

const languagesVarsCodes = [
  "LANG_neobooking_research", //Rechercher
];

const SearchBar: React.FC<SearchBarProps> = ({
  isDateRange = true,
  showDuration = false,
  isMultiDestination = false,
  showChildren = true,
  showBabies = true,
  isSearching = false,
  handler,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  //Search bar states & setter
  const {
    destinations,
    setDestinations,
    duration,
    setDuration,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    roomCategory,
    setRoomCategory,
    product,
    setProduct,
    nbAdults,
    setNbAdults,
    nbChildren,
    setNbChildren,
    nbBabies,
    setNbBabies,
    childrensAges,
    setChildrensAges,
  } = useSearchBar();

  /* Destination functions */
  const handleChangeDestination = (e: any) => {
    if (!e || Array.isArray(e)) {
      setRoomCategory(null);
      setProduct(null);
    }
    setDestinations(e);
  };

  /* Date functions */
  const handleChangeStartDate = (date: Date | null) => {
    setStartDate(date);
    setEndDate(
      date
        ? new Date(addDaysToDate(date, showDuration && duration ? duration : 7))
        : new Date(
            addDaysToDate(new Date(), showDuration && duration ? duration : 7)
          )
    );
  };

  const handleChangeEndDate = (date: Date | null) => {
    setEndDate(date);
  };

  /* Date range function */
  const handleChangeDateRangePicker = (date: [Date | null, Date | null]) => {
    setStartDate(date[0]);
    setEndDate(date[1]);
  };

  /* PAX function */
  const onAdultPaxChange = (symbol: string) => {
    if (symbol === "-") {
      setNbAdults(nbAdults ? nbAdults - 1 : 1);
    } else {
      setNbAdults(nbAdults ? nbAdults + 1 : 2);
    }
  };

  const onChildrenPaxChange = (symbol: string) => {
    if (symbol === "-") {
      setNbChildren(nbChildren ? nbChildren - 1 : 0);
      //on supprime le dernier element du tableau childrenAges
      const newArray = [...childrensAges].slice(0, -1);
      setChildrensAges(newArray);
    } else {
      setNbChildren(nbChildren ? nbChildren + 1 : 1);
      const newArray = [...childrensAges, 1];
      setChildrensAges(newArray);
    }
  };

  const onBabiesPaxChange = (symbol: string) => {
    if (symbol === "-") {
      setNbBabies(nbBabies ? nbBabies - 1 : 0);
    } else {
      setNbBabies(nbBabies ? nbBabies + 1 : 1);
    }
  };

  const handleChangeChildrensAges = (index: number, value: number) => {
    const newArray: number[] = [...childrensAges];
    newArray[index] = value;
    setChildrensAges(newArray);
  };

  const setDefaultDestination = (
    value: SelectOptionType[] | SelectOptionType | null
  ) => {
    setDestinations(value);
  };

  return (
    <>
      <form className="tw-mb-8" onSubmit={handler}>
        <div className="tw-grid tw-grid-cols-12 tw-gap-2">
          {/* Destination info */}
          <div className="tw-col-span-12 sm:tw-col-span-3">
            <SearchDestination
              isMulti={isMultiDestination}
              onChange={handleChangeDestination}
              onDefaultValueSet={setDefaultDestination}
              isClearable={true}
              value={destinations}
            />
          </div>
          {
            /* Destination products & roomTypeCategories list */
            destinations !== null && !Array.isArray(destinations) && (
              <>
                <div className="tw-col-span-12 sm:tw-col-span-4">
                  <SearchRoomTypeCategory
                    etabId={parseInt(destinations.value)}
                    value={roomCategory}
                    setRoomCategory={setRoomCategory}
                  />
                </div>
              </>
            )
          }
          {/* Date info */}
          <SearchDates
            isDateRange={isDateRange}
            showDuration={showDuration}
            onStartDateChange={handleChangeStartDate}
            onEndDateChange={handleChangeEndDate}
            onDurationChange={(e) => setDuration(e.target.value)}
            onDateRangePickerChange={handleChangeDateRangePicker}
            duration={duration ?? 7}
            startDate={startDate}
            endDate={endDate}
          />
          {/* Pax info */}
          <div className="tw-col-span-12 sm:tw-col-span-3">
            <SearchPax
              adultPaxValue={nbAdults ?? 2}
              onAdultPaxChange={onAdultPaxChange}
              minAdult={1}
              showChildren={showChildren}
              childrenPaxValue={nbChildren}
              onChildrenPaxChange={onChildrenPaxChange}
              minChildren={0}
              showBabies={showBabies}
              babiesPaxValue={nbBabies}
              onBabiesPaxChange={onBabiesPaxChange}
              minBabies={0}
              childrensAge={childrensAges}
              handleChangeChildrensAges={handleChangeChildrensAges}
            />
          </div>
          {/* Button submit */}
          <div className="tw-col-span-12 sm:tw-col-span-2">
            <Button
              type="submit"
              isLoading={isSearching}
              shape="outline"
              additionalClassname={
                "tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center "
              }
            >
              <span className="tw-truncate">
                <FontAwesomeIcon className="tw-mr-2" icon={faSearch} />
                {getLanguageVarValue("LANG_neobooking_research")}
              </span>
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

SearchBar.propTypes = {
  //
};

export default SearchBar;
